import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  icon?: React.ReactNode;
  className?: string;
  activeColor?: string;
}

export const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  icon,
  className = '',
  activeColor = 'purple',
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const colorClasses = {
    purple: {
      ring: 'focus:ring-purple-500 focus:border-purple-500',
      hover: 'hover:border-purple-300 dark:hover:border-purple-600',
      icon: 'text-purple-500',
      selected: 'bg-purple-50 dark:bg-purple-900/20',
      check: 'text-purple-600 dark:text-purple-400',
    },
    indigo: {
      ring: 'focus:ring-indigo-500 focus:border-indigo-500',
      hover: 'hover:border-indigo-300 dark:hover:border-indigo-600',
      icon: 'text-indigo-500',
      selected: 'bg-indigo-50 dark:bg-indigo-900/20',
      check: 'text-indigo-600 dark:text-indigo-400',
    },
    blue: {
      ring: 'focus:ring-blue-500 focus:border-blue-500',
      hover: 'hover:border-blue-300 dark:hover:border-blue-600',
      icon: 'text-blue-500',
      selected: 'bg-blue-50 dark:bg-blue-900/20',
      check: 'text-blue-600 dark:text-blue-400',
    },
  };

  const colors = colorClasses[activeColor as keyof typeof colorClasses] || colorClasses.purple;

  return (
    <div ref={dropdownRef} className="relative">
      {/* Select Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full pl-10 pr-10 py-3 text-sm font-medium bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl ${colors.ring} text-gray-900 dark:text-white cursor-pointer transition-all duration-200 ${colors.hover} hover:shadow-md text-left ${className}`}
      >
        {selectedOption?.label || placeholder}
      </button>

      {/* Left Icon */}
      {icon && (
        <div
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${colors.icon} pointer-events-none`}
        >
          {icon}
        </div>
      )}

      {/* Chevron Icon */}
      <ChevronDown
        className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;

              return (
                <motion.button
                  key={option.value}
                  type="button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-sm font-medium text-left flex items-center gap-3 transition-all duration-150 ${
                    isSelected
                      ? `${colors.selected} ${colors.check}`
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {/* Option Icon */}
                  {option.icon && (
                    <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                      {option.icon}
                    </div>
                  )}

                  {/* Option Label */}
                  <span className="flex-1">{option.label}</span>

                  {/* Check Icon */}
                  {isSelected && <Check className="h-4 w-4 flex-shrink-0" />}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
